import React, { useContext, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useIntl } from '@edx/frontend-platform/i18n';

import { AppDataContext } from '../../context';
import type { CourseExtendedCatalogueItem } from '../../types';
import { isEligibleToEnrollV2 } from '../../utils';
import { useProfileCourses, useDebounce, useCourseCatalogue } from '../../hooks';
import { ArrowRightIcon } from '../../img/icons';
import SpinLoader from '../SpinLoader';
import CourseInProgressCard from './CourseInProgressCard';
import { findImmediateNextChallengeCourse } from './utils';

import './index.scss';
import messages from './messages';

interface CoursesInProgressProps {
  showOnProgress?: boolean;
  showAll?: boolean;
  title?: string;
}

const enrollmentLink: React.FC = (courseId: string) => (
  <NavLink to={`/course-overview/${courseId}`} className="c_button">
    <ArrowRightIcon />
  </NavLink>
);

const CoursesInProgress: React.FC<CoursesInProgressProps> = ({ showOnProgress = true, title = '', showAll = false }) => {
  const [filteredCourseData, setFilteredCourseData] = useState<any[]>([]);
  const [eligibilityStatus, setEligibilityStatus] = useState<{ [key: string]: boolean }>({});
  const [completedStatus, setCompletedStatus] = useState<{ [key: string]: boolean }>({});
  const [progressStatus, setProgressStatus] = useState<{ [key: string]: boolean }>({});
  const [detailsLoaded, setDetailsLoaded] = useState<{ [key: string]: boolean }>({});
  const [courseStatusChecked, setCourseStatusChecked] = useState<boolean>(false);
  const [coursesToDisplay, setCoursesToDisplay] = useState<any[]>([]);
  const [fiveDayCoursesList, setFiveDayCoursesList] = useState<any[]>([]);
  const [fiveDayCoursesCompletedStatus, setFiveDayCoursesCompletedStatus] = useState<{ [key: string]: boolean }>({});
  const [fiveDayCoursesStatusChecked, setFiveDayCoursesStatusChecked] = useState<boolean>(false);

  const intl = useIntl();
  const { authenticatedUser, allowedCourses } = useContext(AppDataContext);
  const {
    userCoursesCompletions, isUserCoursesCompletionsLoaded, getCourseCompletions, getCourseGrade,
  } = useProfileCourses(authenticatedUser);
  const [allCoursesData, , allCourseMetaDataLoaded] = useCourseCatalogue(allowedCourses);

  const firstOfAllowedCourseId: string = allowedCourses[0]?.course_id;

  const handleCheckCoursesStatus = async (): Promise<void> => {
    if ((showAll && !allCourseMetaDataLoaded) || !isUserCoursesCompletionsLoaded) {
      return;
    }

    if (Array.isArray(userCoursesCompletions)) {
      setCourseStatusChecked(false);
      setFiveDayCoursesStatusChecked(false);

      const validCoursesIDs = allowedCourses
        .filter((pre) => !pre.type)
        .map((course) => course.course_id);

      const coursesToFilter = allCoursesData
        .filter((course) => validCoursesIDs.includes(course?.search?.data?.id || ''))
        .map((course) => ({
          course: {
            course_id: course?.search?.data?.id || '',
            course_name: course?.search?.data?.content?.display_name || '',
          },
          search: course?.search,
          metadata: course?.metadata,
        }));

      const newFilteredCourseData: CourseExtendedCatalogueItem[] = validCoursesIDs
        .map((courseID: string) => (
          coursesToFilter.find((pre) => pre?.course?.course_id === courseID)
        ))
        .filter((course): course is NonNullable<typeof course> => course !== undefined);

      setFilteredCourseData(newFilteredCourseData);

      if (newFilteredCourseData.length === 0) {
        setCourseStatusChecked(true);
        setFiveDayCoursesStatusChecked(true);
        return;
      }

      for (const courseInstance of newFilteredCourseData) {
        const index: number = newFilteredCourseData.indexOf(courseInstance);
        const courseId = courseInstance?.course?.course_id;
        if (courseId) {
          const preCourseId = courseInstance?.metadata?.pre_requisite_courses[0] || '';
          const preCourseCompletion = getCourseCompletions(preCourseId);
          const preCourseGrade = getCourseGrade(preCourseId);
          await isEligibleToEnrollV2( // eslint-disable-line no-await-in-loop
            courseInstance?.metadata?.pre_requisite_courses[0],
            null,
            true,
            authenticatedUser,
            preCourseCompletion,
            preCourseGrade,
          )
            .then((eligibleData: boolean | { eligible: boolean, completed: boolean }) => {
              if (typeof eligibleData !== 'boolean') {
                setEligibilityStatus((prev) => ({
                  ...prev,
                  [courseId]: courseId === firstOfAllowedCourseId ? true : eligibleData.eligible,
                }));
              }
              setDetailsLoaded((prev) => ({
                ...prev,
                [courseId]: true,
              }));
            });
          const courseCompletion = getCourseCompletions(courseId);
          const courseGrade = getCourseGrade(courseId);
          await isEligibleToEnrollV2( // eslint-disable-line no-await-in-loop
            courseId,
            null,
            true,
            authenticatedUser,
            courseCompletion,
            courseGrade,
          )
            .then((eligibleData: boolean | { eligible: boolean, completed: boolean, progress: boolean }) => {
              if (typeof eligibleData !== 'boolean') {
                setCompletedStatus((prev) => ({
                  ...prev,
                  [courseId]: eligibleData.completed,
                }));
                setProgressStatus((prev) => ({
                  ...prev,
                  [courseId]: eligibleData.progress,
                }));
                if (index === newFilteredCourseData.length - 1) {
                  setCourseStatusChecked(true);
                }
              }
            });
        }
      }
    } else {
      setCourseStatusChecked(true);
      setFiveDayCoursesStatusChecked(true);
    }
  };

  useEffect(() => {
    handleCheckCoursesStatus();
  }, [isUserCoursesCompletionsLoaded, allCourseMetaDataLoaded]);

  const handleCourseToDisplay = () => {
    if (courseStatusChecked && filteredCourseData.length > 0) {
      const validCoursesIDs = allowedCourses.filter((pre) => !pre.type)
        .map((course) => (course.course_id));
      const coursesToShowcase = showAll
        ? validCoursesIDs
        : Object.keys(completedStatus).filter((courseId: string) => (
          showOnProgress
            ? !completedStatus[courseId] && progressStatus[courseId]
            : completedStatus[courseId]
        ));

      if (coursesToShowcase.length === 0) {
        setCourseStatusChecked(true);
        setFiveDayCoursesStatusChecked(true);
        return;
      }

      setCoursesToDisplay(coursesToShowcase);
      const challenge5dayCoursesList = coursesToShowcase.map((courseID) => ({
        ...findImmediateNextChallengeCourse(courseID, allowedCourses),
        parentCourseID: courseID,
      }));
      setFiveDayCoursesList(challenge5dayCoursesList);
    }
  };

  const handle5DayChallengeCourseToDisplay = async () => {
    const filteredFiveDayCoursesList = fiveDayCoursesList.filter((pre) => pre?.course_id);
    if (fiveDayCoursesList.length > 0) {
      if (filteredFiveDayCoursesList.length > 0) {
        for (const challenge5dayChallengeCourse of filteredFiveDayCoursesList) {
          const index = filteredFiveDayCoursesList.indexOf(challenge5dayChallengeCourse);
          const courseCompletion = getCourseCompletions(challenge5dayChallengeCourse?.course_id);
          const courseGrade = getCourseGrade(challenge5dayChallengeCourse?.course_id);
          await isEligibleToEnrollV2( // eslint-disable-line no-await-in-loop
            challenge5dayChallengeCourse?.course_id,
            null,
            true,
            authenticatedUser,
            courseCompletion,
            courseGrade,
          )
            .then((eligibleData: boolean | { eligible: boolean, completed: boolean }) => {
              if (typeof eligibleData !== 'boolean') {
                setFiveDayCoursesCompletedStatus((prev) => ({
                  ...prev,
                  [challenge5dayChallengeCourse?.course_id]: eligibleData.completed,
                }));

                if (index === filteredFiveDayCoursesList.length - 1) {
                  setFiveDayCoursesStatusChecked(true);
                }
              }
            });
        }
      } else {
        setFiveDayCoursesStatusChecked(true);
      }
    }
  };

  useDebounce(handleCourseToDisplay, 200, [courseStatusChecked]);

  useDebounce(handle5DayChallengeCourseToDisplay, 200, [fiveDayCoursesList]);

  const filteredCoursesToDisplay = showAll
    ? filteredCourseData
    : filteredCourseData
      .filter((courseInstance) => coursesToDisplay.includes(courseInstance?.course?.course_id));

  if (!fiveDayCoursesStatusChecked || !isUserCoursesCompletionsLoaded || !allCourseMetaDataLoaded) {
    return (
      <SpinLoader size={45} containerHeight="200px" />
    );
  }

  if (coursesToDisplay.length === 0) {
    return null;
  }

  return (
    <div className="courses-in-progress">
      <div className="modules recklessneue-book-normal-congress-blue-38px">
        {title || intl.formatMessage(messages.title)}
      </div>
      <div className="courses-slider" data-name="courses-in-progress">
        {filteredCoursesToDisplay.map((courseInstance, index) => (
          <CourseInProgressCard
            key={`course-item-${courseInstance?.course?.course_id}`}
            courseInstance={courseInstance}
            index={index}
            fiveDayCoursesCompletedStatus={fiveDayCoursesCompletedStatus}
            fiveDayCoursesList={fiveDayCoursesList}
            eligibilityStatus={eligibilityStatus}
            completedStatus={completedStatus}
            detailsLoaded={detailsLoaded}
            enrollmentLink={enrollmentLink}
          />
        ))}
      </div>
    </div>
  );
};

export default CoursesInProgress;
